import React from "react";
import { withStyles, Divider, Grid } from "@material-ui/core";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import styles from "./styles";
import CustomRadioGroup from "../GroupMsg/Components/CustomRadioGroup";
import {
  getDataWithQueryString,
  postData,
} from "../../../../../api/componentAPIs";
import appConstants from "../../../../../appConstants";
import { sortBy } from "lodash";
import MESSAGES from "../../../../../appMessages";
import { AppLoading } from "../../../../../loaders/loaders";
import { trackEvent } from "../../../../common/AppInsightsEventTracking";
import Events from "../../../../../utilities/EventEmitter";
import Bobtail from "./DispatchBobtail";
import DispatchTrailer from "./DispatchTrailer";
import { PanelHeader } from "./components/index";
import { getDriverError } from "./utils";
import  DispatchInProgress from "./DispatchInProgress";
import constants from "./constants";
import WithDrawer from "../../Common/Drawer/WithDrawer"
import EquipmentDefects from "./EquipmentDefects";
import { fetchDriverImage } from "../../utils/utils";
import SafetyData from "./SafetyData";

class DispatchPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDispatchType: appConstants.DISPATCH_TYPE.TRAILER,
      isDataLoaded: false,
      dispatchError: null,
      driver: props.driver ?? {},
      dispatchInProgress: false,
      dispatchingDelayed: false,
      showEquipementDefect: null,
      driversImage: {},
      isDriverSafetyOk: false,
    };

    this.trailerDetailsList = [];
    this.routeList = [];
    this.tractorList = [];
    this.dollyList = [];
    this.trailerList = [];
    this.timer = null;
    this.container = React.createRef();
    this.dispatchedDriver = null;
  }
  componentDidMount() {
    this.getDispatchPanelAPIs();
    Events.on("DriverDispatched", this.checkDriverDispatchedStatus);
    Events.on("RefreshEquipmentData", this.getDispatchPanelAPIs);
    trackEvent("Dispatch_Panel", this.props.userProfile, { event: "Viewed" });
    this.setDriverImage(this.state.driver.driverNumber);
  }

  componentWillUnmount() {
    Events.removeListener("DriverDispatched", this.checkDriverDispatchedStatus);
    Events.removeListener("RefreshEquipmentData", this.getDispatchPanelAPIs);
  }

  componentDidUpdate(_prevProps) {
    if (!this.props.driver && this.state.driver.driverId) {
      const driverError = getDriverError(
        this.getSelectedDriverData(this.state.driver.driverId)
      );
      if (this.state.driver.driverError !== driverError) {
        this.setState((prevState) => ({
          driver: {
            ...prevState.driver,
            driverError: driverError,
          },
        }));
      }
    }
  }

  onDispatchProgressTimeOut = () => {
    this.timer = setTimeout(() => {
      this.setState({ dispatchingDelayed: true });
    }, appConstants.ONE_MINUTE_TIME_OUT);
  };

  checkDriverDispatchedStatus = (data) => {
    const { success, message, driverId } = data;
    if (this.dispatchedDriver?.toUpperCase() !== driverId?.toUpperCase()) {
      // if dispatch update event is not for driver dispatched by current user skip showing success/failure notification
      return;
    }
    const { driverName } = this.state.driver;
    if (success) {
      this.props.enqueueSnackbar(`${driverName} dispatched successfully. `, {
        variant: "success",
      });
      this.props.onClose();
    } else {
      this.props.enqueueSnackbar(`Dispatching ${driverName} failed`, {
        variant: "error",
      });
      this.dispatchedDriver = null;
      this.setState({
        dispatchInProgress: false,
        dispatchingDelayed: false,
        dispatchError: JSON.stringify(message.error),
      });
    }
    clearTimeout(this.timer);
  };

  getDispatchPanelAPIs = () => {
    const terminalCode = this.props.terminalCode;
    Promise.all([
      getDataWithQueryString("GetAllEquipment", `terminalCode=${terminalCode}`),
      getDataWithQueryString(
        "GetAllRouteNamesForTerminal",
        `terminalCode=${terminalCode}`
      ),
    ]).then(([allEquipmentRes, routeListRes]) => {
      if (allEquipmentRes.status === 200 || routeListRes.status === 200) {
        const { allEquipment, routeList } = {
          allEquipment: allEquipmentRes?.data ?? [],
          routeList: routeListRes?.data ?? [],
        };

        const trailerStatusInOrder = constants.TRAILER_STATUS_ALLOWED;
        // sort by trailernumber
        this.trailerDetailsList = sortBy(allEquipment.trailer, [
          (item) => trailerStatusInOrder.indexOf(item.trailerStatus),
          (item) => item.trailerNumber,
        ]);

        this.generateDataOptions(allEquipment, routeList);

        this.setState({
          isDataLoaded: true,
        });
      }
      if (allEquipmentRes.status !== 200 || routeListRes.status !== 200) {
        this.handleError();
      }
    });
  };

  sortByLabel = (optionsList = []) => {
    return sortBy(optionsList, [(item) => parseInt(item.label)]);
  };

  generateDataOptions = (allEquipments, routeList) => {
    this.routeList = this.sortByLabel(
      routeList.map((item) => ({
        label: item.routeName,
        value: item.routeName,
        labelSuffix: Boolean(item?.description)
          ? ` - ${item.description.trim()}`
          : "",
      }))
    );

    const { dolly = [], tractor = [] } = allEquipments;
    this.tractorList = this.sortByLabel(
      tractor
        .filter(
          (item) =>
            !constants.TRACTOR_STATUS_TO_EXCLUDE.includes(item.tractorStatus)
        )
        .map(
          ({
            tractorNumber,
            tractorStatus,
            tractorId,
            isStraightTruck,
            defects,
          }) => ({
            label: tractorNumber,
            value: tractorId,
            labelSuffix: `(${tractorStatus})`,
            tractorStatus,
            isStraightTruck,
            defects,
          })
        )
    );

    this.dollyList = this.sortByLabel(
      dolly.map(({ dollyNumber, dollyStatus, dollyId, defects }) => ({
        label: dollyNumber,
        value: dollyId,
        labelSuffix: `(${dollyStatus})`,
        dollyStatus,
        defects,
      }))
    );

    this.trailerList = this.getSortedTrailerList(this.trailerDetailsList);

    this.driversList = this.props.allAvailableDriverList.map(
      ({ driverName, driverNumber, driverId }) => ({
        label: driverName,
        value: driverId,
        labelSuffix: `#${driverNumber}`,
      })
    );
  };

  getSortedTrailerList = (trailer = []) => {
    if (!trailer.length) {
      return trailer;
    }

    const trailerStatusInOrder = constants.TRAILER_STATUS_ALLOWED;

    // filter by allowed trailer status and sort by trailer status order
    return trailer
      .filter((item) => trailerStatusInOrder.includes(item.trailerStatus))
      .map(
        ({
          trailerNumber,
          trailerId,
          trailerStatus,
          trailerType,
          defects,
          routeName,
        }) => ({
          label: trailerNumber,
          value: trailerId,
          labelSuffix: `(${trailerStatus})`,
          trailerStatus,
          trailerType,
          defects,
          routeName: routeName?.slice(-3),
        })
      );
  };

  handleError = () => {
    this.props.enqueueSnackbar(MESSAGES.COMMON.LOADING_DATA, {
      variant: "error",
    });
    this.setState({
      isDataLoaded: true,
    });
  };

  handleRadioChange = (value) => {
    this.setState({
      selectedDispatchType: value,
      dispatchError: null,
    });
  };

  dispatchDriver = (data) => {
    const driverError = getDriverError(
      this.getSelectedDriverData(this.state.driver.driverId)
    );
    if (driverError || !data) {
      this.setState((prevState) => ({
        driver: {
          ...prevState.driver,
          driverError: driverError,
        },
      }));
      return;
    }
    const reqParams = {
      terminalCode: this.props.terminalCode,
      driverId: this.state.driver.driverId,
      eventDateTime: new Date(),
      ...data,
    };
    this.setState(
      {
        dispatchInProgress: true,
        dispatchError: null,
      },
      this.onDispatchProgressTimeOut
    );
    trackEvent("Dispatch_Panel", this.props.userProfile, {
      event: "Dispatch_Driver",
      eventData: reqParams,
    });
    postData("DispatchDriver", reqParams).then(
      (response) => {
        if (response.status >= 200 && response.status < 400) {
          this.dispatchedDriver = this.state.driver.driverId;
        } else {
          this.handleDispatchError();
        }
      },
      (_error) => {
        this.handleDispatchError();
      }
    );
  };

  handleDispatchError = () => {
    this.props.enqueueSnackbar(`Error while dispatching driver`, {
      variant: "error",
    });
    clearTimeout(this.timer);
    this.setState({
      dispatchInProgress: false,
      dispatchingDelayed: false,
    });
  };

  isDriverPreSelected = () => {
    return Boolean(this.props.driver?.driverId);
  };

  getSelectedDriverData = (driverId) => {
    const { allAvailableDriverList } = this.props;
    return allAvailableDriverList.find(
      (driver) => driver.driverId === driverId
    );
  };

  handleDriverSelection = (value) => {
    const selectedDriverData = this.getSelectedDriverData(value?.value);
    const { driverId, driverName, driverNumber, safetyData } =
      selectedDriverData;
    if (!driverNumber) {
      return;
    }

    this.setState({
      driver: {
        driverId,
        driverName,
        driverNumber,
        driverError: getDriverError(selectedDriverData),
        safetyData,
      },
      isDriverSafetyOk: false,
    });
    this.setDriverImage(driverNumber);
  };

  closeTrailerDefectModal = () => {
    this.setState({
      showEquipementDefect: null,
    });
  };

  openDvirReports = (equipmentType) => () => {
    this.setState({
      showEquipementDefect: equipmentType,
    });
  };

  setDriverImage = async (driverNumber) => {
    if (!driverNumber) {
      return;
    }
    let driverImage = this.state.driversImage[driverNumber];
    if (!driverImage) {
      driverImage = (
        await fetchDriverImage([driverNumber], this.props.terminalCode)
      )[0]?.driverImage;
      this.setState((prevState) => ({
        ...prevState,
        driversImage: {
          ...prevState.driversImage,
          [driverNumber]: driverImage,
        },
      }));
    }
  };

  updateDriverSafetyOk = (e) => {
    this.setState({ isDriverSafetyOk: e.target.checked })
  }

  renderSafteyMessage = () => {
    if (this.state.driver.safetyData?.length) {
      return (
        <SafetyData
          safetyData={this.state.driver.safetyData}
          isOK={this.state.isDriverSafetyOk}
          onIsOKCLick={this.updateDriverSafetyOk}
        />
      );
    }
    return null;
  }


  render() {
    const { classes } = this.props;
    const isPreselectedDriver = this.isDriverPreSelected();
    const { selectedDispatchType, isDriverSafetyOk,driver } = this.state;

    if (!this.state.isDataLoaded) {
      return (
        <div id="appContainer" className={classes.layout}>
          <AppLoading />
        </div>
      );
    }
    return (
      <div ref={this.container} className={classes.container}>
        <PanelHeader
          isPreselectedDriver={isPreselectedDriver}
          data={driver}
          handleDriverSelection={this.handleDriverSelection}
          availableDriverOptions={this.driversList}
          dispatchInProgress={this.state.dispatchInProgress}
          driverImage={this.state.driversImage[driver.driverNumber]}
        />
        <Divider className={classes.headerDivider} />
        <div className={classes.contentContainer}>
          <Grid container spacing={8} direction="row" className={classes.main}>
            <Grid container>
              <Grid item xs={12} className={classes.headerStyle}>
                DISPATCH WITH
              </Grid>
              <Grid item xs={12} className={classes.radioGroupStyle}>
                <CustomRadioGroup
                  variant="filled"
                  onChange={this.handleRadioChange}
                  labels={[
                    {
                      value: appConstants.DISPATCH_TYPE.TRAILER,
                      labelText: "Trailer",
                    },
                    {
                      value: appConstants.DISPATCH_TYPE.BOBTAIL,
                      labelText: "Bob Tail",
                    },
                  ]}
                  defaultValue={selectedDispatchType}
                />
              </Grid>
            </Grid>
          </Grid>

          {selectedDispatchType === appConstants.DISPATCH_TYPE.TRAILER ? (
            <DispatchTrailer
              allTrailers={this.trailerDetailsList}
              trailerList={this.trailerList}
              routeList={this.routeList}
              dollyList={this.dollyList}
              tractorList={this.tractorList}
              cancel={this.props.onClose}
              dispatchDriver={this.dispatchDriver}
              dispatchError={this.state.dispatchError}
              isPreselectedDriver={isPreselectedDriver}
              openDvirReports={this.openDvirReports}
              driver={driver}
              disableDispatch={driver.safetyData?.length && !isDriverSafetyOk}
            >
              {this.renderSafteyMessage()}
            </DispatchTrailer>
          ) : (
            <Bobtail
              tractorList={this.tractorList}
              routeList={this.routeList}
              cancel={this.props.onClose}
              dispatchDriver={this.dispatchDriver}
              dispatchError={this.state.dispatchError}
              openDvirReports={this.openDvirReports}
              disableDispatch={driver.safetyData?.length && !isDriverSafetyOk}
            >
              {this.renderSafteyMessage()}
            </Bobtail>
          )}

          {this.state.dispatchInProgress ? (
            <DispatchInProgress
              close={this.props.onClose}
              delayed={this.state.dispatchingDelayed}
            />
          ) : null}
          {this.state.showEquipementDefect && (
            <EquipmentDefects
              userProfile={this.props.userProfile}
              onCancel={this.closeTrailerDefectModal}
              defaultEquipmentType={this.state.showEquipementDefect}
            />
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  WithDrawer,
  withStyles(styles),
  withSnackbar
)(DispatchPanel);
